import { ChevronLeftOutlined, ChevronRight } from '@mui/icons-material'
import { Alert, Box, Button, Collapse, Paper, Typography } from '@mui/material'
import { memo, useCallback, useMemo, useState } from 'react'

import { MuiToolbarAlertBannerProps } from '../../../utils/muiTable/materialReactTableUtils.ts'
import { getNestedValue, MrtMobileCard } from './MrtMobileCard.tsx'
import { MrtMobileFilterDrawer } from './MrtMobileFilterDrawer.tsx'
import { MrtMobileLoadingOverlay } from './MrtMobileLoadingOverlay.tsx'
import { MrtMobileToolbar } from './MrtMobileToolbar.tsx'
import { MrtMobileListViewProps } from './mrtTypes.ts'

/**
 * Obtenemos el id del registro
 * @param row
 * @param index
 */
const getFallbackRowId = <T extends Record<string, any>>(row: T, index: number): string => {
  const record = row as Record<string, unknown>
  return String(record._id ?? record.id ?? index)
}

const matchesSearch = <T extends Record<string, any>>(row: T, search: string, fields?: string[]): boolean => {
  const normalized = search.trim().toLowerCase()

  if (!normalized) {
    return true
  }

  if (!fields?.length) {
    return JSON.stringify(row).toLowerCase().includes(normalized)
  }

  return fields.some((field) => {
    const value = getNestedValue(row, field)

    return String(value ?? '')
      .toLowerCase()
      .includes(normalized)
  })
}

const MAX_MOBILE_SKELETONS = 6
const DEFAULT_SKELETON_FIELDS = 4

/**
 * Vista de lista para dispositivos móviles.
 * @author isi-template
 */
function MrtMobileListViewComponent<T extends Record<string, any>>({
  config,
  tableData,
  totalCount,
  isLoading,
  isFetching,
  refetch,
  renderRowActions,
  table,
}: MrtMobileListViewProps<T>) {
  const mobileConfig = config.mobileConfig
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false)
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({})
  const handlePageChange = useCallback(
    (pageIndex: number) => {
      table.setPageIndex(pageIndex) // MRT se encarga de todo por detrás
    },
    [table],
  )
  const { pagination, columnFilters, globalFilter, showAlertBanner } = table.getState()

  // Obtenemos las props del banner usando tu función
  const alertProps = MuiToolbarAlertBannerProps(showAlertBanner)

  const displayData = useMemo(() => {
    if (!mobileConfig || mobileConfig.isServerSearch || !mobileConfig.enableSearch || !globalFilter.trim()) {
      return tableData
    }

    return tableData.filter((row) => matchesSearch(row, globalFilter, mobileConfig.searchableFields))
  }, [tableData, mobileConfig, globalFilter])

  const mrtRows = table.getRowModel().rows

  const mrtRowMap = useMemo(() => {
    const map = new Map<string, (typeof mrtRows)[number]>()

    for (const row of mrtRows) {
      map.set(row.id, row)
    }

    return map
  }, [mrtRows])

  const activeFiltersCount = columnFilters.length + (globalFilter.trim() ? 1 : 0)

  const selectedCount = table.getSelectedRowModel().flatRows.length

  const toggleExpanded = useCallback((rowId: string) => {
    setExpandedRows((previous) => ({
      ...previous,
      [rowId]: !previous[rowId],
    }))
  }, [])

  const mobileAlertBanner = (
    <Collapse in={showAlertBanner}>
      <Box sx={{ px: 0, pb: 1 }}>{alertProps && <Alert {...alertProps} sx={{ borderRadius: 1.5 }} />}</Box>
    </Collapse>
  )

  const totalPages = Math.max(1, Math.ceil(totalCount / Math.max(pagination.pageSize, 1)))

  /**
   * Tanto la carga inicial como el cambio
   * de página/refetch muestran skeletons.
   *
   * No mostramos los datos anteriores debajo
   * del skeleton porque esto provoca una sensación
   * de overlay y puede generar saltos visuales.
   */
  const showSkeleton = isLoading || isFetching

  /**
   * No generamos demasiadas tarjetas skeleton.
   *
   * Aunque pageSize sea 20, 50 o 100,
   * mostramos como máximo 6.
   */
  const skeletonCount = Math.min(Math.max(pagination.pageSize, 1), MAX_MOBILE_SKELETONS)

  /**
   * Intentamos que el skeleton represente
   * la misma cantidad de campos que la tarjeta.
   */
  const skeletonFields = mobileConfig?.summaryFields?.length ?? DEFAULT_SKELETON_FIELDS

  if (showSkeleton) {
    return (
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
        }}
      >
        <MrtMobileToolbar
          title={config.title}
          config={mobileConfig!}
          table={table}
          activeFiltersCount={activeFiltersCount}
          selectedCount={selectedCount}
          isFetching={isFetching}
          refetch={refetch}
          onOpenFilters={() => setFilterDrawerOpen(true)}
          customActions={config.renderTopToolbarCustomActions?.({
            table,
            refetch,
            data: tableData,
          })}
        />

        {/* ------------------------------------------------------------------ */}
        {/* ERROR BANNER AQUÍ                                                   */}
        {/* ------------------------------------------------------------------ */}
        {mobileAlertBanner}

        {/* -------------------------------------------------------------- */}
        {/* MOBILE SKELETONS                                                */}
        {/* -------------------------------------------------------------- */}

        <MrtMobileLoadingOverlay count={skeletonCount} fields={skeletonFields} columns={2} />

        {/* -------------------------------------------------------------- */}
        {/* PAGINATION                                                      */}
        {/* -------------------------------------------------------------- */}

        {config.manualPagination !== false && totalCount > 0 && (
          <MobilePagination
            pageIndex={pagination.pageIndex}
            totalPages={totalPages}
            isFetching={isFetching}
            onPageChange={handlePageChange}
          />
        )}

        <MrtMobileFilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          config={config}
          table={table}
          filters={columnFilters}
        />
      </Box>
    )
  }

  /** Cuando la lista esta vacía */
  if (displayData.length === 0) {
    return (
      <Box
        sx={{
          width: '100%',
          minWidth: 0,
        }}
      >
        <MrtMobileToolbar
          title={config.title}
          config={mobileConfig!}
          table={table}
          activeFiltersCount={activeFiltersCount}
          selectedCount={selectedCount}
          isFetching={isFetching}
          refetch={refetch}
          onOpenFilters={() => setFilterDrawerOpen(true)}
          customActions={config.renderTopToolbarCustomActions?.({
            table,
            refetch,
            data: tableData,
          })}
        />

        {/** Banner de alerta, tambien para desplegar los errores */}
        {mobileAlertBanner}

        <Paper
          variant="outlined"
          sx={{
            p: 3,
            textAlign: 'center',
            borderRadius: 1.5,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            No hay datos disponibles
          </Typography>
        </Paper>

        <MrtMobileFilterDrawer
          open={filterDrawerOpen}
          onClose={() => setFilterDrawerOpen(false)}
          config={config}
          table={table}
          filters={columnFilters}
        />
      </Box>
    )
  }

  return (
    <Box
      sx={{
        width: '100%',
        minWidth: 0,
      }}
    >
      <MrtMobileToolbar
        title={config.title}
        config={mobileConfig!}
        table={table}
        activeFiltersCount={activeFiltersCount}
        selectedCount={selectedCount}
        isFetching={isFetching}
        refetch={refetch}
        onOpenFilters={() => setFilterDrawerOpen(true)}
        customActions={config.renderTopToolbarCustomActions?.({
          table,
          refetch,
          data: tableData,
        })}
      />

      {/** Banner de alerta, tambien para desplegar los errores */}
      {mobileAlertBanner}

      <Box
        sx={{
          width: '100%',
          minWidth: 0,
          display: 'flex',
          flexDirection: 'column',
          /**
           * Separación real entre tarjetas.
           *
           * La tarjeta no necesita conocer
           * nada acerca de su posición dentro
           * de la lista.
           */
          gap: 1,
        }}
      >
        {displayData.map((row, index) => {
          const rowId = getFallbackRowId(row, index)
          const mrtRow = mrtRowMap.get(rowId)
          return (
            <MrtMobileCard
              key={rowId}
              row={row}
              mrtRow={mrtRow}
              config={mobileConfig!}
              table={table}
              selected={mrtRow?.getIsSelected() ?? false}
              selectionEnabled={config.enableSelection === true && (mrtRow?.getCanSelect() ?? true)}
              multiSelection={config.multiSelection ?? true}
              expanded={!!expandedRows[rowId]}
              onToggleExpand={() => toggleExpanded(rowId)}
              renderRowActions={renderRowActions}
              renderDetailPanel={config.renderDetailPanel}
            />
          )
        })}
      </Box>

      {/** Paginación */}
      {config.manualPagination !== false && totalCount > 0 && (
        <MobilePagination
          pageIndex={pagination.pageIndex}
          totalPages={totalPages}
          isFetching={isFetching}
          onPageChange={handlePageChange}
        />
      )}

      {/** Filtros */}
      <MrtMobileFilterDrawer
        open={filterDrawerOpen}
        onClose={() => setFilterDrawerOpen(false)}
        config={config}
        table={table}
        filters={columnFilters}
      />
    </Box>
  )
}

/** Interfaz de paginador */
interface MobilePaginationProps {
  pageIndex: number
  totalPages: number
  isFetching: boolean
  onPageChange: (page: number) => void
}

const MobilePagination = memo(function MobilePagination({
  pageIndex,
  totalPages,
  isFetching,
  onPageChange,
}: MobilePaginationProps) {
  const canPrevious = pageIndex > 0
  const canNext = pageIndex < totalPages - 1

  return (
    <Box
      sx={(theme) => ({
        position: 'sticky',
        bottom: 0,
        zIndex: theme.zIndex.appBar - 1,
        mt: 1.25,
        px: 1,
        py: 0.75,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: 2,
        backgroundColor: theme.palette.background.paper,
        /**
         * Evita que el contenido que queda detrás
         * se mezcle visualmente con el paginador.
         */
        backdropFilter: 'blur(8px)',
        /**
         * Separación visual respecto a las tarjetas.
         */
        boxShadow: theme.shadows[3],
      })}
    >
      <Button
        size="small"
        variant="text"
        startIcon={<ChevronLeftOutlined />}
        disabled={!canPrevious || isFetching}
        onClick={() => onPageChange(pageIndex - 1)}
        sx={{
          minWidth: 0,
          px: 1,
        }}
      >
        Anterior
      </Button>

      <Typography
        variant="body2"
        fontWeight={600}
        whiteSpace="nowrap"
        sx={{
          px: 1,
        }}
      >
        {pageIndex + 1} / {totalPages}
      </Typography>

      <Button
        size="small"
        variant="text"
        endIcon={<ChevronRight />}
        disabled={!canNext || isFetching}
        onClick={() => onPageChange(pageIndex + 1)}
        sx={{
          minWidth: 0,
          px: 1,
        }}
      >
        Siguiente
      </Button>
    </Box>
  )
})

/**
 * Vista principal para listado de datos en formato mobile
 * @author isi-template
 */
export const MrtMobileListView = memo(MrtMobileListViewComponent) as typeof MrtMobileListViewComponent
